'use client';

// BookOneWave — a reusable silky-ribbon waveform component for BookOne.Party.
// Built on the ElevenLabs docs hero shader (Perlin-displaced plane + 7-stop
// gradient fragment) with added: 16-band arc array, speech-envelope talking
// mode, and an SVG-textPath mask for text-shaped wave.
//
// Drop-in usage:
//
//   <BookOneWave palette="bookone" height={400} />
//   <BookOneWave colors={["#FF5500", "#00AAFF"]} arc={0.5} />
//   <BookOneWave talking talkSpeed={2.0} />
//
// Imperative (useful when an external system drives talking, e.g. ElevenLabs
// agent isSpeaking events):
//
//   const ref = useRef<BookOneWaveHandle>(null);
//   <BookOneWave ref={ref} palette="bookone" />
//   useEffect(() => ref.current?.setTalking(agent.isSpeaking), [agent.isSpeaking]);

import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { PALETTES, type PaletteName, type Stop } from './palettes';
import { vertexShader, fragmentShader } from './shaders';

// ─── Public API ──────────────────────────────────────────────────────────────

export type ColorInput = PaletteName | string[] | Stop[];

export interface BookOneWaveProps {
  /** Preset palette name, array of hex strings (2-7), or raw 7-stop Stop[]. */
  palette?: ColorInput;
  /** Alias of `palette` — for API clarity when passing custom hex values. */
  colors?: ColorInput;
  /** −4 … +4 — signed arc amount. Positive arches up, negative sags down. */
  arc?: number;
  /** Toggle speech-waveform animation. Pulses `uAmplitude` smoothly. */
  talking?: boolean;
  /** Multiplier on the talking-mode oscillator frequencies. 0.3 (slow) to 5 (rapid). */
  talkSpeed?: number;
  /** Optional text that shapes the wave into letters via SVG mask. */
  text?: string;
  /** User-controlled amplitude (0.2 – 3). Ignored while `talking` is true. */
  amplitude?: number;
  /** Time multiplier for the Perlin displacement (0 freezes the wave). */
  speed?: number;
  /** Gradient repeat — higher = more bands visible. Default 2. */
  gradientRepeat?: number;
  /** Perlin sampling density — higher = tighter curls. Default 0.25. */
  density?: number;
  /** CSS height. Defaults to a responsive clamp. */
  height?: number | string;
  /** Pixel pref — higher = crisper on retina, higher GPU cost. Default min(devicePixelRatio, 2). */
  pixelRatio?: number;
  className?: string;
  style?: React.CSSProperties;
}

export interface BookOneWaveHandle {
  setTalking: (on: boolean) => void;
  setTalkSpeed: (v: number) => void;
  setArc: (v: number) => void;
  setPalette: (c: ColorInput) => void;
  setText: (s: string) => void;
  setSpeed: (v: number) => void;
  setAmplitude: (v: number) => void;
}

// ─── Stop resolution (preset | hex array | Stop[]) ───────────────────────────

const FIXED_POSITIONS = [0.0, 0.2, 0.3, 0.7, 0.8, 0.9, 1.0];

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [((n >> 16) & 0xff) / 255, ((n >> 8) & 0xff) / 255, (n & 0xff) / 255];
};
const rgbToHex = (r: number, g: number, b: number) => {
  const h = (c: number) => Math.round(c * 255).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
};

// Build 7 stops at the fixed gradient positions from 2-7 user hex colours.
// Interpolates linearly in RGB. For a seamless looping gradient, the caller
// should pass the same colour first and last (the silky ribbon loops on vUv.y).
function interpolateStops(colors: string[]): Stop[] {
  if (colors.length === 0) throw new Error('BookOneWave: at least one colour required');
  const rgbs = colors.map(hexToRgb);
  return FIXED_POSITIONS.map((pos) => {
    // Map stop position back into the user's colour array.
    const t = pos * (rgbs.length - 1);
    const i0 = Math.floor(t);
    const i1 = Math.min(i0 + 1, rgbs.length - 1);
    const f = t - i0;
    const a = rgbs[i0]!;
    const b2 = rgbs[i1]!;
    const r = a[0] + (b2[0] - a[0]) * f;
    const g = a[1] + (b2[1] - a[1]) * f;
    const b = a[2] + (b2[2] - a[2]) * f;
    return { color: rgbToHex(r, g, b), alpha: 1, position: pos };
  });
}

function resolveStops(input: ColorInput | undefined): Stop[] {
  if (!input) return PALETTES.bookone;
  if (typeof input === 'string') return PALETTES[input as PaletteName] ?? PALETTES.bookone;
  if (input.length === 0) return PALETTES.bookone;
  if (typeof input[0] === 'string') return interpolateStops(input as string[]);
  return input as Stop[];
}

// ─── Component ───────────────────────────────────────────────────────────────

const ARC_PEAK_RATIO = 0.06;
const NUM_BANDS = 16;

const toVec4 = (stop: Stop) => {
  const c = new THREE.Color(stop.color);
  c.convertLinearToSRGB();
  return new THREE.Vector4(c.r, c.g, c.b, stop.alpha);
};
const toUniformStops = (stops: Stop[]) =>
  stops.map(s => ({ color: toVec4(s), position: s.position }));

export const BookOneWave = forwardRef<BookOneWaveHandle, BookOneWaveProps>(function BookOneWave(
  {
    palette,
    colors,
    arc = 0,
    talking = false,
    talkSpeed = 1,
    text,
    amplitude = 1.3,
    speed = 1,
    gradientRepeat = 2,
    density = 0.25,
    height,
    pixelRatio,
    className,
    style,
  },
  ref,
) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  // Live state refs — props land here so the rAF loop reads the latest values
  // without re-running the three.js setup on every prop change.
  const stateRef = useRef({
    arc,
    talking,
    talkSpeed,
    text: text ?? '',
    amplitude,
    speed,
    gradientRepeat,
    density,
    stops: resolveStops(colors ?? palette),
    targetStops: resolveStops(colors ?? palette),
  });

  // Keep refs in sync whenever props change.
  useEffect(() => { stateRef.current.arc = arc; }, [arc]);
  useEffect(() => { stateRef.current.talking = talking; }, [talking]);
  useEffect(() => { stateRef.current.talkSpeed = talkSpeed; }, [talkSpeed]);
  useEffect(() => { stateRef.current.text = text ?? ''; }, [text]);
  useEffect(() => { stateRef.current.amplitude = amplitude; }, [amplitude]);
  useEffect(() => { stateRef.current.speed = speed; }, [speed]);
  useEffect(() => { stateRef.current.gradientRepeat = gradientRepeat; }, [gradientRepeat]);
  useEffect(() => { stateRef.current.density = density; }, [density]);
  useEffect(() => { stateRef.current.targetStops = resolveStops(colors ?? palette); }, [colors, palette]);

  useImperativeHandle(
    ref,
    () => ({
      setTalking:   (v) => { stateRef.current.talking = v; },
      setTalkSpeed: (v) => { stateRef.current.talkSpeed = v; },
      setArc:       (v) => { stateRef.current.arc = v; },
      setPalette:   (c) => { stateRef.current.targetStops = resolveStops(c); },
      setText:      (s) => { stateRef.current.text = s; },
      setSpeed:     (v) => { stateRef.current.speed = v; },
      setAmplitude: (v) => { stateRef.current.amplitude = v; },
    }),
    [],
  );

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // WebGL can fail to initialize for several reasons that we hit on real
    // installs: iOS Safari WebViews under memory pressure, browsers with
    // hardware acceleration disabled, or sandbox/extension blockers. Wrap
    // construction in try/catch so the whole component degrades to an empty
    // host div instead of throwing inside React render and unmounting the
    // tree above it. The imperative-handle methods (setPalette, setTalking,
    // etc.) below stay registered as no-ops on stateRef so callers can
    // continue invoking them safely.
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (err) {
      console.warn('[bookone-wave] WebGL unavailable — wave hidden:', err);
      return;
    }
    renderer.setPixelRatio(pixelRatio ?? Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    host.appendChild(renderer.domElement);

    // Context-loss containment. iOS Safari raises this when memory pressure
    // forces the GPU context away (common in in-app WebViews — Instagram,
    // TikTok). preventDefault tells the browser we'll handle restoration; we
    // pause the rAF loop so subsequent setUniform / render calls don't throw
    // against the missing context.
    let contextLost = false;
    const onContextLost = (e: Event) => {
      e.preventDefault();
      contextLost = true;
      console.warn('[bookone-wave] WebGL context lost — animation paused');
    };
    const onContextRestored = () => {
      contextLost = false;
      console.warn('[bookone-wave] WebGL context restored');
    };
    renderer.domElement.addEventListener('webglcontextlost', onContextLost);
    renderer.domElement.addEventListener('webglcontextrestored', onContextRestored);

    const scene = new THREE.Scene();
    let zoom = 120;
    const computeZoom = () => Math.max(36, Math.min(180, host.clientWidth / 9.6));
    const makeCamera = () => {
      const w = host.clientWidth, h = host.clientHeight;
      const halfW = w / (2 * zoom), halfH = h / (2 * zoom);
      const cam = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, -100, 100);
      cam.position.z = 0;
      return cam;
    };
    let camera = makeCamera();

    const initialStopUniforms = toUniformStops(stateRef.current.stops);
    const uniforms = {
      uTime:           { value: 0 },
      uAmplitude:      { value: stateRef.current.amplitude },
      uDensity:        { value: stateRef.current.density },
      uSpeed:          { value: 0.09 * stateRef.current.speed },
      uGradientRepeat: { value: stateRef.current.gradientRepeat },
      uGradientStops:  { value: initialStopUniforms },
      uArcPeak:        { value: 0 },
      uViewHalf:       { value: 6 },
      uArcs:           { value: new Array(NUM_BANDS).fill(0) },
    };

    const material = new THREE.ShaderMaterial({
      vertexShader, fragmentShader, uniforms, side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(24, 10, 128, 64), material);
    mesh.rotation.set(Math.PI / 2, -Math.PI, 0);
    scene.add(mesh);

    // Live gradient-stops tracker (lerps to target each frame for smooth palette changes).
    const liveStops = initialStopUniforms.map(s => ({
      color: s.color.clone(),
      position: s.position,
    }));
    uniforms.uGradientStops.value = liveStops;

    // Text-shape mask (applied directly to the host element) ────────────────
    const buildMask = (txt: string, a: number): string | null => {
      const trimmed = txt.trim();
      if (!trimmed) return null;
      const escaped = trimmed.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const vbW = 1600, vbH = 560;
      const aTune = Math.min(1, Math.abs(a));
      const padding = 140 + 40 * aTune;
      const fontSize = Math.max(110, 300 - 120 * aTune);
      const maxTextLen = vbW - padding * 2 - 180 * aTune;
      const naturalW = trimmed.length * fontSize * 0.62;
      const squeeze = aTune > 0.02 || naturalW > maxTextLen;
      const textLenAttr = squeeze
        ? ` textLength="${Math.min(naturalW, maxTextLen)}" lengthAdjust="spacingAndGlyphs"`
        : '';
      const textAttrs = `font-family="Arial Black, Impact, Helvetica, sans-serif" font-weight="900" font-size="${fontSize}" fill="black"${textLenAttr}`;
      const baselineY = vbH / 2;
      const peakRiseVb = ARC_PEAK_RATIO * vbW * a;
      const peakY = baselineY - 2 * peakRiseVb;
      const arcPath = `M ${padding} ${baselineY} Q ${vbW / 2} ${peakY} ${vbW - padding} ${baselineY}`;
      const body =
        `<defs><path id="p" d="${arcPath}"/></defs>` +
        `<text ${textAttrs}><textPath href="#p" startOffset="50%" text-anchor="middle">${escaped}</textPath></text>`;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vbW} ${vbH}" preserveAspectRatio="xMidYMid meet">${body}</svg>`;
      return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
    };

    const applyMask = () => {
      const m = buildMask(stateRef.current.text, stateRef.current.arc);
      host.style.webkitMaskImage = m ?? 'none';
      host.style.maskImage = m ?? 'none';
    };

    // Resize ────────────────────────────────────────────────────────────────
    const resize = () => {
      const w = host.clientWidth, h = host.clientHeight;
      renderer.setSize(w, h, false);
      zoom = computeZoom();
      camera = makeCamera();
      uniforms.uViewHalf.value = w / (2 * zoom);
      uniforms.uArcPeak.value = (ARC_PEAK_RATIO * w) / zoom;
      // Dynamically center the mesh so the ribbon always sits in the visual
      // middle of the frame, at any width × height. halfH is the camera's
      // visible Y range; positioning the mesh at 0.4 × halfH offsets the
      // natural low-bias of the gradient-colored surface's projection, so
      // the wave's center of mass lands at screen midline whether the
      // container is 280px tall, 680px tall, mobile, desktop, or embed.
      const halfH = h / (2 * zoom);
      mesh.position.y = halfH * 0.4;
      applyMask();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(host);
    window.addEventListener('orientationchange', resize);
    resize();

    // Animate ───────────────────────────────────────────────────────────────
    const clock = new THREE.Clock();
    let lastText = stateRef.current.text;
    let lastArc = stateRef.current.arc;
    let rafId = 0;

    const animate = () => {
      // Skip render passes while the GPU context is lost — calling render()
      // against a missing context throws and crashes the rAF loop.
      if (contextLost) {
        rafId = requestAnimationFrame(animate);
        return;
      }
      const t = clock.getElapsedTime();
      const s = stateRef.current;

      // Rebuild mask only when text or arc changes (cheap check).
      if (s.text !== lastText || s.arc !== lastArc) {
        applyMask();
        lastText = s.text;
        lastArc = s.arc;
      }

      uniforms.uTime.value = t;
      uniforms.uSpeed.value = 0.09 * s.speed;
      uniforms.uDensity.value = s.density;
      uniforms.uGradientRepeat.value = s.gradientRepeat;

      // Amplitude target: talking-envelope or user-amplitude.
      let ampTarget: number;
      const arr = uniforms.uArcs.value;
      if (s.talking) {
        const ts = Math.max(0.1, s.talkSpeed);
        const breath = 0.5 + 0.5 * Math.sin(t * 0.55 * ts + Math.sin(t * 1.2 * ts) * 0.4);
        const phrase = 0.5 + 0.5 * Math.sin(t * 1.9  * ts + Math.cos(t * 0.7 * ts));
        const micro  = 0.5 + 0.5 * Math.sin(t * 4.3  * ts + Math.sin(t * 3.1 * ts) * 0.6);
        const envelope = 0.35 + 0.65 * breath * phrase;
        ampTarget = envelope * (1.3 + 1.4 * micro);
        // Bands decay to 0 so the wave stays along its baseline.
        for (let i = 0; i < NUM_BANDS; i++) arr[i] += (0 - arr[i]) * 0.18;
      } else {
        const hasText = !!s.text.trim();
        ampTarget = hasText ? Math.max(s.amplitude, 1.8) : s.amplitude;
        // Slider arc → bands weighted by parabola.
        for (let i = 0; i < NUM_BANDS; i++) {
          const x = (i + 0.5) / NUM_BANDS;
          const weight = 4 * x * (1 - x);
          const target = s.arc * weight;
          arr[i] += (target - arr[i]) * 0.15;
        }
      }
      const ampLerp = s.talking
        ? Math.min(0.45, 0.12 * Math.sqrt(Math.max(0.1, s.talkSpeed)))
        : 0.08;
      uniforms.uAmplitude.value += (ampTarget - uniforms.uAmplitude.value) * ampLerp;

      // Palette lerp.
      const tgt = toUniformStops(s.targetStops);
      for (let i = 0; i < liveStops.length; i++) {
        const live = liveStops[i]!;
        const target = tgt[i]!;
        live.color.lerp(target.color, 0.03);
        live.position = target.position;
      }

      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener('orientationchange', resize);
      renderer.domElement.removeEventListener('webglcontextlost', onContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', onContextRestored);
      renderer.dispose();
      material.dispose();
      mesh.geometry.dispose();
      if (renderer.domElement.parentNode === host) host.removeChild(renderer.domElement);
    };
  }, [pixelRatio]);  // pixelRatio is the only construction-time prop; everything else is ref-driven

  return (
    <div
      ref={hostRef}
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        height: height ?? 'clamp(220px, 38vw, 540px)',
        overflow: 'hidden',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        ...style,
      }}
    />
  );
});

export default BookOneWave;
